import { IconDownload, IconFileUpload } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import {
  Badge, Button, DatePickerInput, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  FileDropzone, Group, IconAction, Loader, Stack, Text, Textarea,
} from "@/components/ui";
import { useDescargarDocumento, useReemplazarDocumento } from "@/features/documentos/hooks/use-documentos-mutations";
import { documentosApi } from "@/features/documentos/services/documentos.api";
import { qk } from "@/lib/query-keys";
import { formatIso, hoyNegocio, parseIso } from "@/features/datos-generales/utils/iso-date";
import type { DocumentoVinculado } from "@/lib/types";

/** Extensiones aceptadas al reemplazar · las mismas que al subir (sin .zip: se reemplaza UN archivo). */
const ACCEPT_POR_CATEGORIA: Record<string, string> = {
  Adicional: ".pdf,.png,.jpg,.jpeg,.xml,.docx,.xlsx",
  Evidencia: ".pdf,.png,.jpg,.jpeg",
  Contrato: ".pdf",
};

interface VersionesDocumentoDialogProps {
  abierto: boolean;
  onCerrar: () => void;
  solicitudId: string;
  documento: DocumentoVinculado;
  /** El usuario puede reemplazar (dueño, estado de carga, categoría reemplazable). */
  puedeReemplazar: boolean;
  /** Fase 4 · el documento maneja vigencia: la versión nueva exige su fecha de emisión. */
  requiereFechaEmision?: boolean;
}

/**
 * Gestión Documental · historial de versiones de un documento + reemplazo con motivo.
 * La versión anterior NUNCA se borra: queda descargable desde aquí (spec D-1/D-15).
 */
export function VersionesDocumentoDialog({ abierto, onCerrar, solicitudId, documento, puedeReemplazar, requiereFechaEmision = false }: VersionesDocumentoDialogProps) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [motivo, setMotivo] = useState("");
  const [fechaEmision, setFechaEmision] = useState("");
  const descargar = useDescargarDocumento(solicitudId);
  const reemplazar = useReemplazarDocumento(solicitudId, documento.documentoId);

  const versiones = useQuery({
    queryKey: qk.versionesDocumento(documento.documentoId),
    queryFn: () => documentosApi.versiones(solicitudId, documento.documentoId),
    enabled: abierto,
  });

  const cerrar = () => {
    setArchivo(null);
    setMotivo("");
    setFechaEmision("");
    onCerrar();
  };

  const valido =
    archivo !== null && motivo.trim().length > 0 && motivo.trim().length <= 500 && (!requiereFechaEmision || fechaEmision !== "");

  return (
    <Dialog open={abierto} onOpenChange={(o) => (!o ? cerrar() : undefined)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Versiones del documento</DialogTitle>
          <DialogDescription>{documento.tipoDocumento ?? documento.nombreOriginal}</DialogDescription>
        </DialogHeader>

        <Stack gap="xs">
          {versiones.isLoading ? (
            <Loader size="sm" />
          ) : (
            (versiones.data ?? []).map((v) => (
              <Group key={v.numero} justify="space-between" wrap="nowrap" className="rounded-md border border-border p-2">
                <div className="min-w-0 flex-1">
                  <Group gap="xs" wrap="nowrap">
                    <Badge size="xs" color={v.esVigente ? "multiva" : "slate"}>v{v.numero}</Badge>
                    {v.esVigente ? <Badge size="xs" color="exito">Vigente</Badge> : null}
                    <Text size="sm" truncate>{v.nombreOriginal}</Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {dayjs(v.cargadoAt).format("DD/MM/YYYY HH:mm")} · {v.subidoPorNombre ?? "—"}
                  </Text>
                  {v.motivo ? <Text size="xs" c="dimmed">Motivo: {v.motivo}</Text> : null}
                  {v.fechaEmision ? <Text size="xs" c="dimmed">Emitido el {dayjs(v.fechaEmision).format("DD/MM/YYYY")}</Text> : null}
                </div>
                <IconAction
                  label="Descargar esta versión"
                  variant="subtle"
                  size="sm"
                  loading={descargar.isPending}
                  onClick={() =>
                    descargar.mutate({
                      id: documento.documentoId,
                      nombre: v.esVigente ? v.nombreOriginal : `v${v.numero}_${v.nombreOriginal}`,
                      version: v.numero,
                    })
                  }
                >
                  <IconDownload size={14} stroke={1.7} />
                </IconAction>
              </Group>
            ))
          )}
        </Stack>

        {puedeReemplazar ? (
          <form
            className="mt-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (valido && archivo)
                reemplazar.mutate(
                  { file: archivo, motivo: motivo.trim(), fechaEmision: requiereFechaEmision ? fechaEmision : undefined },
                  { onSuccess: () => { setArchivo(null); setMotivo(""); setFechaEmision(""); } }
                );
            }}
          >
            <Stack gap="sm">
              <Text size="sm" fw={600}>Reemplazar (nueva versión)</Text>
              <FileDropzone
                accept={ACCEPT_POR_CATEGORIA[documento.categoria] ?? ".pdf"}
                ctaTexto={archivo ? archivo.name : "Seleccionar archivo"}
                hint="La versión actual se conserva en el historial."
                Icon={IconFileUpload}
                onFile={setArchivo}
              />
              {requiereFechaEmision ? (
                <DatePickerInput
                  label="Fecha de emisión del nuevo documento"
                  description="Su vigencia se calcula a partir de esta fecha."
                  required
                  maxDate={hoyNegocio()}
                  value={parseIso(fechaEmision)}
                  onChange={(d) => setFechaEmision(formatIso(d))}
                />
              ) : null}
              <Textarea
                label="Motivo del reemplazo"
                required
                maxLength={500}
                rows={2}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </Stack>
            <DialogFooter className="mt-4">
              <Button type="button" variant="default" onClick={cerrar}>Cerrar</Button>
              <Button type="submit" disabled={!valido} loading={reemplazar.isPending}>Guardar nueva versión</Button>
            </DialogFooter>
          </form>
        ) : (
          <DialogFooter className="mt-4">
            <Button type="button" variant="default" onClick={cerrar}>Cerrar</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
